/**
 * useDailyCheckGate — decides whether the current DRIVER must complete today's
 * Daily Vehicle Check before using the app. Admins, owners and super admins are
 * never locked. Backed by persisted data only (company_settings +
 * vehicle_daily_checks), re-evaluated on every route change so refresh,
 * reopen, back and deep links all re-check.
 *
 * "Today" = UK calendar date (Europe/London), not a rolling 24 hours.
 * On a read error the gate stays open (existing behaviour) so a network blip
 * can never lock drivers out of in-progress work.
 */
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { resolveActiveCompanyId } from "@/lib/activeCompany";
import { useUserRole } from "@/hooks/useUserRole";
import { getUKToday, isDailyCheckGateLocked } from "@/lib/dailyCheckQuestions";

interface DailyCheckGateResult {
  /** true = driver, company requires checks, no check today */
  gateLocked: boolean;
  loading: boolean;
  recheck: () => void;
}

export function useDailyCheckGate(): DailyCheckGateResult {
  const { isDriver, isAdmin, loading: roleLoading } = useUserRole();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [requireChecks, setRequireChecks] = useState(false);
  const [checkDoneToday, setCheckDoneToday] = useState(false);
  const [recheckCounter, setRecheckCounter] = useState(0);

  const appliesToUser = isDriver && !isAdmin;

  useEffect(() => {
    if (roleLoading) return;
    if (!appliesToUser) { setRequireChecks(false); setLoading(false); return; }
    // Once cleared for today, don't re-query on every navigation.
    if (checkDoneToday) return;
    let cancelled = false;

    async function check() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const companyId = await resolveActiveCompanyId(user.id);
        if (!companyId) return;

        const { data: settings, error: sErr } = await supabase
          .from("company_settings")
          .select("require_daily_checks")
          .eq("company_id", companyId)
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (sErr) throw sErr;

        const required = settings?.require_daily_checks === true;
        setRequireChecks(required);
        if (!required) return;

        const { count, error: cErr } = await supabase
          .from("vehicle_daily_checks")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("check_date", getUKToday());
        if (cancelled) return;
        if (cErr) throw cErr;
        setCheckDoneToday((count ?? 0) > 0);
      } catch (err) {
        console.error("[DailyCheckGate] Error:", err);
        if (!cancelled) setRequireChecks(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    check();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheckCounter, roleLoading, appliesToUser, location.pathname]);

  return {
    gateLocked: isDailyCheckGateLocked({ requireChecks, isDriver, isAdmin, checkDoneToday }),
    loading: roleLoading || loading,
    recheck: () => setRecheckCounter((c) => c + 1),
  };
}
